import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../api.js";
import { getStaffStudentGoals } from "../studentGoals.js";
import { getStaffRepertoire } from "../repertoire.js";
import { useAsync } from "../useAsync.js";
import { Drawer } from "../ui/Dialog.js";
import { StudentProgressSnapshot } from "./StudentProgressSnapshot.js";
import "../studentProgressSnapshot.css";

export type StudentTimelineCategory =
  | "teaching"
  | "practice"
  | "attendance"
  | "booking"
  | "milestone"
  | "admin";

export interface StudentTimelineEvent {
  id: string;
  occurredAt: string;
  category: StudentTimelineCategory;
  kind: string;
  title: string;
  summary: string | null;
  actorName: string | null;
  href: string | null;
  visibility: "staff" | "family";
}

interface TimelinePage {
  events: StudentTimelineEvent[];
  nextCursor: string | null;
  coverage: {
    booking: "ok" | "unavailable";
    warning: string | null;
  };
}

type TimelineFilter = "all" | Exclude<StudentTimelineCategory, "admin">;

const FILTERS: Array<{ id: TimelineFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "teaching", label: "Teaching" },
  { id: "practice", label: "Practice" },
  { id: "attendance", label: "Attendance" },
  { id: "booking", label: "Bookings" },
  { id: "milestone", label: "Milestones" },
];

const progressDate = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "Europe/London",
});

export function formatProgressWhen(value: string): string {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? progressDate.format(parsed) : value;
}

function categoryLabel(category: StudentTimelineCategory): string {
  switch (category) {
    case "teaching":
      return "Teaching";
    case "practice":
      return "Practice";
    case "attendance":
      return "Attendance";
    case "booking":
      return "Booking";
    case "milestone":
      return "Milestone";
    case "admin":
      return "Admin";
  }
}

function timelinePath(studentId: string, filter: TimelineFilter, cursor?: string): string {
  const query = new URLSearchParams({ category: filter, limit: "25" });
  if (cursor) query.set("cursor", cursor);
  return `/admin/students/${encodeURIComponent(studentId)}/progress?${query.toString()}`;
}

function compareEvents(a: StudentTimelineEvent, b: StudentTimelineEvent): number {
  const time = Date.parse(b.occurredAt) - Date.parse(a.occurredAt);
  return time !== 0 ? time : a.id.localeCompare(b.id);
}

export function StudentProgress({ studentId }: { studentId: string }) {
  return <StudentProgressContent key={studentId} studentId={studentId} />;
}

function StudentProgressContent({ studentId }: { studentId: string }) {
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [goalCreateRequest, setGoalCreateRequest] = useState(0);
  const [repertoireCreateRequest, setRepertoireCreateRequest] = useState(0);
  const [goalRevision, setGoalRevision] = useState(0);
  const goals = useAsync(() => getStaffStudentGoals(studentId), [studentId]);
  const repertoire = useAsync(() => getStaffRepertoire(studentId), [studentId]);
  const { visibleEvents, warning, problem, loading } = useTimeline(studentId, "all", goalRevision);

  return <div className="student-progress-workspace">
    <div className="student-progress-toolbar">
      <div><h2>Progress at a glance</h2><p className="small muted">Current direction, recent work and the next teaching step.</p></div>
      <div className="student-progress-toolbar-actions">
        <button type="button" className="quiet" onClick={() => setRepertoireCreateRequest((value) => value + 1)}>Add repertoire</button>
        <button type="button" onClick={() => setGoalCreateRequest((value) => value + 1)}>Add goal</button>
      </div>
    </div>
    <StudentProgressSnapshot studentId={studentId} goalCreateRequest={goalCreateRequest} repertoireCreateRequest={repertoireCreateRequest}
      goals={goals} repertoire={repertoire} onGoalsChanged={() => setGoalRevision((value) => value + 1)} />
    <section className="student-progress-recent" aria-labelledby="student-progress-recent-heading">
      <div className="student-progress-summary-head"><h3 id="student-progress-recent-heading">Recent activity</h3><button type="button" className="student-progress-detail-link" onClick={() => setTimelineOpen(true)}>Full timeline</button></div>
      {warning && <p className="small muted" role="status">{warning}</p>}
      {problem && <p className="small" role="alert">{problem}</p>}
      {loading && visibleEvents.length === 0 ? <p className="small muted" role="status">Loading progress…</p> : visibleEvents.length === 0 ? <p className="small muted">No progress activity is recorded yet.</p> : <ol className="student-progress-recent-list">
        {visibleEvents.slice(0, 3).map((event) => <li key={event.id}><details>
          <summary><time className="small muted" dateTime={event.occurredAt}>{formatProgressWhen(event.occurredAt)}</time><div><strong>{event.title}</strong><span className="small muted">{categoryLabel(event.category)}</span></div></summary>
          <div className="student-progress-recent-copy">{event.summary && <p>{event.summary}</p>}{event.actorName && <span className="small muted">{event.actorName}</span>}{event.href && <Link className="small" to={event.href}>View details</Link>}{!event.summary && !event.actorName && !event.href && <p className="small muted">{categoryLabel(event.category)} activity recorded {formatProgressWhen(event.occurredAt)}.</p>}</div>
        </details></li>)}
      </ol>}
    </section>
    {timelineOpen && <Drawer title="Progress timeline" size="large" className="student-progress-drawer" onClose={() => setTimelineOpen(false)}>
      <StudentProgressTimeline studentId={studentId} goalRevision={goalRevision} />
    </Drawer>}
  </div>;
}

function useTimeline(studentId: string, filter: TimelineFilter, goalRevision: number) {
  const [events, setEvents] = useState<StudentTimelineEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const previousFilter = useRef(filter);

  useEffect(() => {
    let active = true;
    const generation = ++requestGeneration.current;
    setLoading(true);
    setLoadingMore(false);
    setProblem(null);
    setNextCursor(null);
    setWarning(null);
    if (previousFilter.current !== filter) {
      setEvents([]);
      previousFilter.current = filter;
    }

    get<TimelinePage>(timelinePath(studentId, filter))
      .then((page) => {
        if (!active || generation !== requestGeneration.current) return;
        setEvents(page.events);
        setNextCursor(page.nextCursor);
        setWarning(page.coverage.warning);
      })
      .catch((cause: unknown) => {
        if (!active || generation !== requestGeneration.current) return;
        setProblem(cause instanceof Error ? cause.message : "Could not load progress.");
      })
      .finally(() => {
        if (active && generation === requestGeneration.current) setLoading(false);
      });

    return () => {
      active = false;
      requestGeneration.current += 1;
    };
  }, [filter, goalRevision, studentId]);

  const visibleEvents = useMemo(() => {
    const seen = new Set<string>();
    return [...events]
      .sort(compareEvents)
      .filter((event) => {
        if (seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      });
  }, [events]);

  async function loadOlder() {
    if (!nextCursor || loadingMore) return;
    const generation = requestGeneration.current;
    const cursor = nextCursor;
    setLoadingMore(true);
    setProblem(null);
    try {
      const page = await get<TimelinePage>(timelinePath(studentId, filter, cursor));
      if (generation !== requestGeneration.current) return;
      setEvents((current) => {
        const seen = new Set(current.map((event) => event.id));
        return [...current, ...page.events.filter((event) => !seen.has(event.id))];
      });
      setNextCursor(page.nextCursor);
      setWarning(page.coverage.warning);
    } catch (cause: unknown) {
      if (generation !== requestGeneration.current) return;
      setProblem(cause instanceof Error ? cause.message : "Could not load older progress.");
    } finally {
      if (generation === requestGeneration.current) setLoadingMore(false);
    }
  }

  return { visibleEvents, warning, problem, loading, nextCursor, loadingMore, loadOlder };
}

function StudentProgressTimeline({ studentId, goalRevision }: { studentId: string; goalRevision: number }) {
  const [filter, setFilter] = useState<TimelineFilter>("all");
  const { visibleEvents, warning, problem, loading, nextCursor, loadingMore, loadOlder } = useTimeline(studentId, filter, goalRevision);
  return (

      <section className="student-progress" aria-labelledby="student-progress-heading">
        <div className="student-record-section-head student-progress-heading">
          <div>
            <h2 id="student-progress-heading">Progress timeline</h2>
            <p className="small muted">
              A chronological view of meaningful teaching, practice, milestones, attendance and booking activity.
            </p>
          </div>
        </div>

        <div className="student-progress-filters" role="group" aria-label="Filter progress">
          {FILTERS.map((option) => (
            <button
              type="button"
              className="quiet small"
              aria-pressed={filter === option.id}
              key={option.id}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {warning && (
          <div className="notice student-progress-coverage" role="status">
            <p className="small">{warning}</p>
          </div>
        )}

        {problem && (
          <div className="error student-progress-error" role="alert">
            {problem}
          </div>
        )}

        {loading ? (
          <p className="empty">Loading progress…</p>
        ) : visibleEvents.length === 0 ? (
          !problem && <p className="empty">
            {filter === "all"
              ? "No progress activity is recorded yet."
              : `No ${
                  FILTERS.find((item) => item.id === filter)?.label.toLowerCase() ?? "matching"
                } activity is recorded yet.`}
          </p>
        ) : (
          <ol className="student-progress-list">
            {visibleEvents.map((event) => (
              <li className="student-progress-row" key={event.id}>
                <div className="student-progress-row-meta small muted">
                  <span>{categoryLabel(event.category)}</span>
                  <time dateTime={event.occurredAt}>{formatProgressWhen(event.occurredAt)}</time>
                </div>
                <div className="student-progress-row-copy">
                  <strong>{event.title}</strong>
                  {event.summary && <p>{event.summary}</p>}
                  {event.actorName && <span className="small muted">{event.actorName}</span>}
                  {event.href && (
                    <Link className="small" to={event.href}>
                      View details
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {nextCursor && (
          <div className="student-progress-more">
            <button type="button" className="quiet" disabled={loadingMore} onClick={() => void loadOlder()}>
              {loadingMore ? "Loading…" : "Load older activity"}
            </button>
          </div>
        )}
      </section>
  );

}
